// packages/web/src/components/RemedialTaskView.tsx — misma lógica (carga lazy, firma → PUT blob → confirmar, reintento).
// Cambios: el ciclo se muestra como 3 pasos numerados (Descargar · Completar · Subir) con el paso
// actual destacado; zona de subida táctil grande; correcciones de la IA como lista clara.
// Se ve dentro del reporte del alumno (móvil) y del docente; usa tamaños aptos para ambos.
import { useCallback, useEffect, useState } from 'react';
import { Download, Upload, CheckCircle2, RotateCcw, Loader2, RefreshCw } from 'lucide-react';
import type { RemedialTaskResponse } from '@marzano/contracts';
import { REMEDIAL_UPLOAD_ALLOWED_MIMES, REMEDIAL_UPLOAD_MAX_BYTES } from '@marzano/contracts';
import { api, ApiError } from '../api';
import { Alert } from './ui/Alert';
import { Button } from './ui/Button';

interface Props {
  sessionId: string;
  onRetryReady?: (retrySessionId: string) => void;
}
type UploadPhase = 'idle' | 'signing' | 'uploading' | 'confirming';
const MAX_MB = REMEDIAL_UPLOAD_MAX_BYTES / 1024 / 1024;

export function RemedialTaskView({ sessionId, onRetryReady }: Props) {
  const [task, setTask] = useState<RemedialTaskResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const t = await api.getRemedialTask(sessionId);
      setTask(t);
      if (onRetryReady && t.validation_status === 'ok' && t.retry_session_id) onRetryReady(t.retry_session_id);
    } catch (err) {
      setLoadErr(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId, onRetryReady]);
  useEffect(() => { void load(); }, [load]);

  const handleFile = useCallback(async (file: File) => {
    setUploadErr(null);
    const mime = file.type as (typeof REMEDIAL_UPLOAD_ALLOWED_MIMES)[number];
    if (!(REMEDIAL_UPLOAD_ALLOWED_MIMES as readonly string[]).includes(mime)) { setUploadErr('Ese formato no sirve. Sube un PDF, un Word o una foto (JPG, PNG, HEIC o WebP).'); return; }
    if (file.size > REMEDIAL_UPLOAD_MAX_BYTES) { setUploadErr(`El archivo pesa más de ${MAX_MB} MB.`); return; }
    try {
      setUploadPhase('signing');
      const sas = await api.getRemedialUploadUrl(sessionId, { mime, size_bytes: file.size });
      setUploadPhase('uploading');
      await api.putRemedialUploadToBlob(sas.upload_url, mime, file);
      setUploadPhase('confirming');
      const updated = await api.confirmRemedialUpload(sessionId, sas.blob_ref);
      setTask(updated);
      if (updated.validation_status === 'ok') {
        if (updated.retry_link_token) { window.location.href = `/entrar/${encodeURIComponent(updated.retry_link_token)}`; return; }
        if (onRetryReady && updated.retry_session_id) onRetryReady(updated.retry_session_id);
      }
    } catch (err) {
      setUploadErr(err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploadPhase('idle');
    }
  }, [sessionId, onRetryReady]);

  if (loading && !task) {
    return (
      <div role="status" className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-4 text-[15px] text-slate-700">
        <Loader2 aria-hidden className="w-5 h-5 animate-spin text-marzano-600 shrink-0" />
        Estamos preparando una guía para ti. Tarda unos segundos.
      </div>
    );
  }
  if (loadErr) {
    return <div className="space-y-3"><Alert kind="error">{loadErr}</Alert><Button variant="secondary" onClick={() => void load()}>Reintentar</Button></div>;
  }
  if (!task) return null;

  const busy = uploadPhase !== 'idle';
  const pendingValidation = task.upload_ready && task.validation_status === 'pending';
  const rejected = task.validation_status === 'rejected';
  const ok = task.validation_status === 'ok' && task.retry_session_id !== null;
  const current = ok ? 4 : task.upload_ready && !rejected ? 3 : task.guide_ready ? 2 : 1;

  const download = () => {
    setDownloading(true);
    void api.remedialGuideUrl(task.session_id)
      .then(({ url }) => window.open(url, '_blank'))
      .catch(() => setLoadErr('No pudimos abrir la guía. Intenta de nuevo.'))
      .finally(() => setDownloading(false));
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[15px] text-slate-700">Ronda {task.round_number}. Completa la guía para volver a intentarlo.</p>
        <Button variant="ghost" size="sm" onClick={() => void load()} leftIcon={<RefreshCw aria-hidden className="w-4 h-4" />}>Actualizar</Button>
      </div>

      <ol className="space-y-3">
        <Step n={1} title="Descarga la guía" state={current > 1 ? 'done' : 'current'}>
          <Button className="min-h-[48px]" leftIcon={<Download aria-hidden className="w-5 h-5" />} disabled={!task.guide_ready} loading={downloading} onClick={download}>
            {task.guide_ready ? 'Descargar guía (PDF)' : 'Preparando la guía…'}
          </Button>
        </Step>

        <Step n={2} title="Complétala" state={current > 2 ? 'done' : current === 2 ? 'current' : 'todo'}>
          <p className="text-[15px] text-slate-700">En tu tablet o impresa, con tus palabras.</p>
        </Step>

        <Step n={3} title="Sube tu trabajo" state={ok ? 'done' : current >= 2 ? 'current' : 'todo'}>
          {ok ? null : busy ? (
            <div role="status" className="rounded-2xl border-2 border-dashed border-marzano-200 bg-marzano-50 p-6 text-center">
              <Loader2 aria-hidden className="w-8 h-8 mx-auto text-marzano-600 mb-2 animate-spin" />
              <p className="text-base font-semibold text-slate-900">
                {uploadPhase === 'signing' ? 'Preparando la subida…' : uploadPhase === 'uploading' ? 'Subiendo tu archivo…' : 'Estamos revisando tu trabajo'}
              </p>
              <p className="text-[13px] text-slate-600 mt-0.5">{uploadPhase === 'confirming' ? 'Puede tardar unos segundos.' : 'No cierres esta ventana.'}</p>
            </div>
          ) : (
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) void handleFile(f); }}
              className={`block min-h-[120px] rounded-2xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-amber-500 ${dragOver ? 'border-marzano-600 bg-marzano-50' : 'border-slate-300 hover:border-marzano-500 hover:bg-slate-50'}`}>
              <input type="file" className="sr-only" accept={REMEDIAL_UPLOAD_ALLOWED_MIMES.join(',')}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }} />
              <Upload aria-hidden className="w-7 h-7 mx-auto text-marzano-600 mb-1.5" />
              <span className="block text-base font-semibold text-slate-900">{rejected ? 'Sube la versión corregida' : 'Toca aquí o arrastra tu archivo'}</span>
              <span className="block text-[13px] text-slate-600 mt-0.5">PDF, Word o foto · hasta {MAX_MB} MB</span>
            </label>
          )}
          {uploadErr && <Alert kind="error" className="mt-2">{uploadErr}</Alert>}
          {pendingValidation && !busy && <Alert kind="info" className="mt-2">Ya subiste tu trabajo. Toca <strong>Actualizar</strong> para ver el resultado.</Alert>}
          {rejected && task.validation_notes && task.validation_notes.length > 0 && (
            <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-[15px] font-semibold text-amber-900">Corrige esto y vuelve a subirlo:</p>
              <ul className="mt-1.5 space-y-1 list-disc pl-5 text-[15px] text-slate-800">{task.validation_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
          )}
        </Step>
      </ol>

      {ok && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 space-y-3">
          <p className="flex items-center gap-2 text-base font-semibold text-emerald-900"><CheckCircle2 aria-hidden className="w-5 h-5" />¡Trabajo aceptado! Ya puedes volver a intentarlo.</p>
          {task.validation_notes && task.validation_notes.length > 0 && (
            <ul className="list-disc pl-5 text-[15px] text-slate-800 space-y-1">{task.validation_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          )}
          {onRetryReady ? (
            <Button className="min-h-[48px]" leftIcon={<RotateCcw aria-hidden className="w-5 h-5" />} onClick={() => task.retry_session_id && onRetryReady(task.retry_session_id)}>Ir al reintento</Button>
          ) : (
            <p className="text-[15px] text-slate-700">Tu profesor te enviará el link del reintento.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Step({ n, title, state, children }: { n: number; title: string; state: 'done' | 'current' | 'todo'; children?: React.ReactNode }) {
  return (
    <li className={`flex gap-3 rounded-2xl border px-4 py-3.5 ${state === 'current' ? 'border-marzano-200 bg-white' : 'border-slate-200 bg-white'} ${state === 'todo' ? 'opacity-60' : ''}`}
      aria-current={state === 'current' ? 'step' : undefined}>
      <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[15px] font-bold ${state === 'done' ? 'bg-emerald-700 text-white' : state === 'current' ? 'bg-marzano-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
        {state === 'done' ? <CheckCircle2 aria-label="Listo" className="w-5 h-5" /> : n}
      </span>
      <div className="flex-1 min-w-0 space-y-2">
        <h3 className="text-base font-semibold text-slate-900 leading-8">{title}</h3>
        {children}
      </div>
    </li>
  );
}
